import { modKey } from './shortcuts';
import { CLASS_COLORS, NEUTRAL_BUTTON_COLOR } from '@/utils/constants';

/**
 * Pizzul's Guide content. Plain words for kids, in the order LTBB teaches. Every key and button
 * here must exist: keys are checked against state/useKeyboardShortcuts.js by guide.test.js.
 */

export const BUILD_STEPS = [
  { title: 'Pick a melody', color: CLASS_COLORS.melody, how: 'Click "Show me melodies" (or the melody filter). Press play on a loop to hear it, then the + to add it. Your beat now uses its speed.' },
  { title: 'Add drums', color: CLASS_COLORS.drums, how: 'Click "Show me drums". Only drums that fit your speed show up. Press + to add one.' },
  { title: 'Add a texture', color: CLASS_COLORS.texture, how: 'Textures fill out the sound. Click "Show me textures" and add one.' },
  { title: 'Add a sound effect', color: CLASS_COLORS.fx, how: 'A crash, riser or hit for flavor. Click "Show me sound effects" and add one.' },
  { title: 'Add a bassline', color: '#39FF14', how: 'Click "Add Knock Engine Bass". Pizzul writes one in your key. Click the sliders on the bass track to change the feel or sound.' },
  { title: 'Make it a song', color: NEUTRAL_BUTTON_COLOR, how: 'Click Auto Arrange (the magic wand) and pick a style, or build it yourself: copy and paste your parts across the timeline.' },
  { title: 'Label your parts', color: NEUTRAL_BUTTON_COLOR, how: 'Click Add section (the flag) to mark your Intro, Verse and Hook.' },
  { title: 'Save or send it', color: NEUTRAL_BUTTON_COLOR, how: 'At a workshop, click Export MP3 and have a grown-up type their email. Signed in? Click Save.' },
];

/** Top bar first, then the slim edit row. `icon` names a lucide icon the Guide renders. */
export const BUTTONS = [
  { icon: 'Square', name: 'Stop', what: 'Stops the song and goes back to the start.' },
  { icon: 'SkipBack', name: 'Back to start', what: 'Returns to measure 1. Enter or Return does the same and keeps the current play/pause state.' },
  { icon: 'SkipBack', name: 'Back 1 bar', what: 'Moves the playhead one bar to the left.' },
  { icon: 'Play', name: 'Play / Pause', what: 'Plays your song. Press again to pause.' },
  { icon: 'Circle', name: 'Record', what: 'Counts in one bar, then records the armed track.' },
  { icon: 'AudioLines', name: 'Metronome', what: 'Opens Click, Count-in and Auto-quantize settings.' },
  { icon: 'Keyboard', name: 'Keys', what: 'Opens the computer keyboard and MIDI controls.' },
  { icon: 'Repeat', name: 'Loop', what: 'Turns looping on or off. Set what loops on the loop strip above the bar numbers.' },
  { icon: 'Gauge', name: 'LCD readout', what: 'Shows BPM, key and bar.beat. Click it to change BPM, key or song length.' },
  { icon: 'SlidersHorizontal', name: 'Mixer', what: 'Opens volume, pan, mute, solo and effects for every track.' },
  { icon: 'Plus', name: 'Add', what: 'Opens Drum grid, Keys, Sampler and Loop library when Producer tools are on.' },
  { icon: 'MousePointer', name: 'Select tool', what: 'Click clips to pick them, drag to move them, drag their edges to trim.' },
  { icon: 'Scissors', name: 'Slice tool', what: 'Click a clip to cut it in two right there.' },
  { icon: 'Copy', name: 'Copy tool', what: 'Click a clip to copy it to the next phrase boundary. Choose Auto, 4 or 8 bars.' },
  { icon: 'BoxSelect', name: 'Select all', what: 'Picks every clip in your song.' },
  { icon: 'ClipboardCopy', name: 'Copy', what: 'Copies the clips you picked.' },
  { icon: 'ClipboardPaste', name: 'Paste', what: 'Puts your copy at the playhead, or the next empty spot so nothing stacks.' },
  { icon: 'BookOpen', name: "Pizzul's Guide", what: 'Open Menu, then choose Pizzul’s Guide.' },
  { icon: 'Plus', name: 'New Beat', what: 'Open Menu, then start a fresh beat. At a workshop it says Next student.' },
  { icon: 'Flag', name: 'Add section', what: 'Labels part of your song: Intro, Verse, Hook, Bridge, Outro.' },
  { icon: 'Minus', name: 'Zoom out and in (- and +)', what: 'Shows more or less of your song at once.' },
  { icon: 'Wand2', name: 'Auto Arrange', what: 'Turns your loops into a full song in one click.' },
  { icon: 'Download', name: 'Export MP3', what: 'Saves your song as an MP3 (or emails it at a workshop).' },
];

/** Keyboard groups. Keys are drawn as key caps; "+" means press together. */
export const keyGroups = (mod = modKey()) => [
  { title: 'Play and move', rows: [
    { keys: ['Space'], what: 'Play or pause' },
    { keys: ['Enter / Return'], what: 'Back to the beginning; keep playing or paused' },
    { keys: ['R'], what: 'Start or stop recording' },
    { keys: ['←', '→'], what: 'Move the playhead 1 bar', either: true },
    { keys: ['Shift', '←'], what: 'Move a quarter of a bar (Shift + → goes right)' },
    { keys: ['1'], what: 'Jump to bar 1 (keys 1 to 9 jump to bars 1 to 9)' },
    { keys: ['L'], what: 'Loop on or off' },
  ] },
  { title: 'Edit clips', rows: [
    { keys: [mod, 'A'], what: 'Select every clip' },
    { keys: [mod, 'C'], what: 'Copy the clips you picked' },
    { keys: [mod, 'V'], what: 'Paste at the playhead' },
    { keys: [mod, 'D'], what: 'Duplicate the clips you picked' },
    { keys: [mod, 'Shift', 'D'], what: 'Duplicate the selected track' },
    { keys: ['Delete'], what: 'Delete the clips you picked (Backspace works too)' },
    { keys: ['Shift', '↑'], what: 'Raise the loops you picked a semitone (Shift + ↓ lowers)' },
    { keys: [mod, 'Z'], what: 'Undo' },
    { keys: [mod, 'Shift', 'Z'], what: 'Redo' },
    { keys: ['Esc'], what: 'Unpick everything, or close a window' },
  ] },
  { title: 'Tools and saving', rows: [
    { keys: ['V'], what: 'Select tool' },
    { keys: ['S'], what: 'Slice tool' },
    { keys: [mod, 'S'], what: 'Save (when signed in)' },
    { keys: [mod, 'E'], what: 'Export MP3' },
    { keys: ['?'], what: "Open Pizzul's Guide" },
  ] },
];

export const MOUSE_MOVES = [
  {move:'Drag across the bar numbers',what:'Selects a section across tracks. Copy or paste into empty measures. Duplicate after inserts room and moves later sections together; Variation lets you leave tracks out.'},
  {move:'Click a named section',what:'Selects its measures up to the next section marker or song end. Copies preserve the full range, including empty space.'},
  {move:'Drag from empty space on a track',what:'Draws a selection box around clips. Hold Shift to add to your selection.'},
  {move:'Drag a top corner of an audio clip',what:'Sets its fade in or fade out. Side edges still trim its length.'},
  { move: 'Click a clip', what: 'Picks it. Shift-click adds or removes a clip from the selection.' },
  { move: 'Click a track name', what: 'Picks every clip on that track.' },
  { move: 'Drag a clip', what: 'Moves it using the Snap selector: 1, half or quarter bar, or Off. Hold Shift to temporarily turn snapping off.' },
  { move: 'Drag a clip edge', what: 'Trims it shorter (or back longer). Hold Shift for small steps.' },
  { move: 'Right-click a clip', what: 'Reverse audio, set exact fade lengths in beats or seconds, Reset fades, Duplicate, Delete or Loop this.' },
  { move: 'Click the bar numbers', what: 'Moves the playhead there.' },
  { move: 'Drag on the loop strip', what: 'The thin strip above the bar numbers. Drag to draw a loop.' },
  { move: 'Drag the loop', what: 'Drag its ends to resize it or its middle to move it. Click it to turn looping on or off.' },
  { move: `${modKey()} + scroll`, what: 'Zooms the timeline in or out.' },
];
